import React, { useEffect, useState, useCallback } from "react";
import client from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Inbox, Mail, Phone, MapPin, MessageCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import { tr } from "../i18n/LangContext";

const STATUS = [["new", "New", "border-brand-red/40 bg-brand-red/10 text-brand-red"], ["contacted", "Contacted", "border-amber-500/40 bg-amber-500/10 text-amber-400"],
  ["converted", "Converted", "border-brand-green/40 bg-brand-green/10 text-brand-green"], ["closed", "Closed", "border-white/15 bg-white/5 text-muted-foreground"]];
const INTEREST = { consultancy: "Consultancy", software: "Software €499/mo", both: "Undecided" };
const ago = (iso) => {
  const h = Math.floor((Date.now() - new Date(iso)) / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const Row = ({ e, onChange }) => {
  const [notes, setNotes] = useState(e.notes || "");
  const [open, setOpen] = useState(false);
  const st = STATUS.find((s) => s[0] === e.status) || STATUS[0];
  const saveNotes = async () => {
    if (notes === (e.notes || "")) return;
    try { const r = await client.patch(`/admin/enquiries/${e.id}`, { notes }); onChange(r.data); toast.success(tr("Note saved")); }
    catch (err) { toast.error(tr("Couldn't save note")); }
  };
  const setStatus = async (status) => {
    try { const r = await client.patch(`/admin/enquiries/${e.id}`, { status }); onChange(r.data); }
    catch (err) { toast.error(tr("Couldn't update")); }
  };
  const wa = e.phone ? `https://wa.me/${e.phone.replace(/\D/g, "")}` : null;
  return (
    <div data-testid={`enquiry-${e.id}`} className={`rounded-2xl border p-5 ${e.status === "new" ? "border-brand-red/25 bg-brand-red/[0.03]" : "border-white/[0.07] bg-white/[0.02]"}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-display text-base font-semibold text-white">{e.name}</h3>
            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-medium ${st[2]}`} data-testid="enquiry-status-badge">{st[1]}</span>
            <span className="rounded-full border border-white/10 px-2 py-0.5 text-[11px] text-muted-foreground">{INTEREST[e.interest] || e.interest}</span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <a href={`mailto:${e.email}`} className="flex items-center gap-1 hover:text-white"><Mail className="h-3.5 w-3.5" /> {e.email}</a>
            {e.phone && <a href={wa} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Phone className="h-3.5 w-3.5" /> {e.phone}</a>}
            {e.city && <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.city}</span>}
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {ago(e.created_at)}</span>
          </div>
        </div>
        <Select value={e.status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-36 border-white/10 bg-white/[0.03] text-xs text-white" data-testid="enquiry-status-select"><SelectValue /></SelectTrigger>
          <SelectContent className="glass-strong border-white/10">
            {STATUS.map(([k, l]) => <SelectItem key={k} value={k} className="text-xs focus:bg-white/5">{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {e.message && <p className="mt-3 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">{e.message}</p>}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {wa && <a href={wa} target="_blank" rel="noreferrer" data-testid="enquiry-whatsapp" className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-brand-green/30 bg-brand-green/10 px-3 text-xs font-medium text-brand-green hover:bg-brand-green/20"><MessageCircle className="h-3.5 w-3.5" /> {tr("WhatsApp")}</a>}
        <a href={`mailto:${e.email}?subject=${encodeURIComponent("Moroccan Brothers — your request")}`} data-testid="enquiry-email" className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-white/12 bg-white/[0.03] px-3 text-xs font-medium text-white hover:bg-white/[0.07]"><Mail className="h-3.5 w-3.5" /> {tr("Reply by email")}</a>
        <button onClick={() => setOpen((o) => !o)} data-testid="enquiry-notes-toggle" className="text-xs text-muted-foreground hover:text-white">{open ? "Hide notes" : e.notes ? "Notes ✓" : "Add note"}</button>
        {e.handled_by && <span className="ml-auto text-[11px] text-muted-foreground">handled by {e.handled_by}</span>}
      </div>
      {open && (
        <textarea data-testid="enquiry-notes" value={notes} onChange={(ev) => setNotes(ev.target.value)} onBlur={saveNotes} rows={3} placeholder={tr("Internal notes — what was said, next step…")}
          className="mt-3 w-full resize-none rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-sm text-white outline-none focus:border-brand-red/50" />
      )}
    </div>
  );
};

export const EnquiriesPanel = ({ onCount }) => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("open");
  const load = useCallback(async () => {
    try { const r = await client.get("/admin/enquiries"); setItems(r.data); onCount?.(r.data.filter((e) => e.status === "new").length); }
    catch (e) { toast.error(tr("Couldn't load enquiries")); }
  }, [onCount]);
  useEffect(() => { load(); }, [load]);
  const update = (u) => setItems((xs) => { const n = xs.map((x) => (x.id === u.id ? u : x)); onCount?.(n.filter((e) => e.status === "new").length); return n; });

  if (!items) return <div className="grid h-40 place-items-center"><div className="h-7 w-7 animate-spin rounded-full border-2 border-brand-red border-t-transparent" /></div>;
  const shown = items.filter((e) => (filter === "open" ? e.status === "new" || e.status === "contacted" : filter === "all" ? true : e.status === filter));
  return (
    <div className="mt-4" data-testid="enquiries-panel">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{shown.length} enquir{shown.length === 1 ? "y" : "ies"}</p>
        <div className="flex gap-1.5">
          {[["open", "Open"], ["converted", "Converted"], ["closed", "Closed"], ["all", "All"]].map(([k, l0]) => { const l = tr(l0); return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`enquiry-filter-${k}`}
              className={`rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${filter === k ? "border-brand-green/35 bg-brand-green/10 text-brand-green" : "border-white/12 bg-white/[0.03] text-muted-foreground hover:text-white"}`}>{l}</button>
          ); })}
        </div>
      </div>
      {shown.length === 0 ? (
        <div className="grid place-items-center rounded-2xl border border-dashed border-white/10 py-16 text-center" data-testid="enquiries-empty">
          <Inbox className="h-8 w-8 text-muted-foreground/50" />
          <p className="mt-3 text-sm text-muted-foreground">{tr("Nothing here. New requests from the public site will appear the moment they're sent.")}</p>
        </div>
      ) : <div className="grid gap-3">{shown.map((e) => <Row key={e.id} e={e} onChange={update} />)}</div>}
    </div>
  );
};
